/** DTL notification-template editor — thin React wrapper over a lazily
 * imported CodeMirror 6 core (same pattern as JsonEditor/JsonEditorWidget:
 * dynamic import, StrictMode-safe cancelled flag, create-once view,
 * imperative ref).
 *
 * Prop reconciliation:
 * - completion data (schemas/blocks/extras) flows through a mutable ref-box —
 *   late-arriving schemas never rebuild the view;
 * - readOnly / diagnostics reconfigure via the core handle;
 * - external `value` changes apply as a single full-doc replace, skipped when
 *   the value matches what we last emitted or the current doc.
 * onChange is debounced ~250ms and flushed on blur/unmount.
 */
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { emptyCompletionData, sourceLabelsOf, type CompletionDataBox } from './completionData';
import type { EditorCoreHandle, EditorSizing } from './editorCore';
import { mergeSchemaPaths, walkSchema } from './schemaPaths';
import type { TemplateCodeEditorHandle, TemplateCodeEditorProps } from './types';

const DEBOUNCE_MS = 250;

function toCssLength(value: number | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return typeof value === 'number' ? `${value}px` : value;
}

export const TemplateCodeEditor = forwardRef<TemplateCodeEditorHandle, TemplateCodeEditorProps>(
  function TemplateCodeEditor(props, ref) {
    const {
      value,
      contextSchemas,
      blocks,
      extraCompletions,
      diagnostics,
      readOnly = false,
      height,
      maxHeight,
    } = props;
    const theme = useTheme();

    const containerRef = useRef<HTMLDivElement>(null);
    const coreRef = useRef<EditorCoreHandle | null>(null);
    const [ready, setReady] = useState(false);
    const [failed, setFailed] = useState(false);
    const [focused, setFocused] = useState(false);

    // ---- completion data ref-box (mutated in place; never rebuilds the view)
    const dataBoxRef = useRef<CompletionDataBox>({ current: emptyCompletionData() });
    const mergedPaths = useMemo(() => {
      const sources = contextSchemas ?? [];
      return mergeSchemaPaths(sources.map((s) => ({ id: s.id, entries: walkSchema(s.schema) })));
    }, [contextSchemas]);
    dataBoxRef.current.current = {
      paths: mergedPaths,
      sourceLabels: sourceLabelsOf(contextSchemas ?? []),
      blocks: blocks ?? [],
      extra: extraCompletions ?? [],
    };

    // ---- latest-props refs for the create-once effect and stable callbacks
    const propsRef = useRef(props);
    propsRef.current = props;
    const paletteRef = useRef(theme.palette);
    paletteRef.current = theme.palette;

    // ---- debounced onChange
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingRef = useRef<string | null>(null);
    const lastEmittedRef = useRef<string>(value);

    const callbacksRef = useRef({
      flush() {
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        const pending = pendingRef.current;
        pendingRef.current = null;
        if (pending !== null && pending !== lastEmittedRef.current) {
          lastEmittedRef.current = pending;
          propsRef.current.onChange(pending);
        }
      },
      docChanged(doc: string) {
        pendingRef.current = doc;
        if (timerRef.current !== null) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          callbacksRef.current.flush();
        }, DEBOUNCE_MS);
      },
      focusChanged(hasFocus: boolean) {
        if (!hasFocus) callbacksRef.current.flush();
        setFocused(hasFocus);
        propsRef.current.onFocusChange?.(hasFocus);
      },
    });

    // ---- create-once mount effect (StrictMode-safe)
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return undefined;
      let cancelled = false;
      // Not a React-rendered node ref — it holds stable callbacks; safe to
      // close over in the cleanup (satisfies react-hooks/exhaustive-deps).
      const callbacks = callbacksRef.current;

      import('./editorCore')
        .then((mod) => {
          if (cancelled) return;
          const p = propsRef.current;
          const sizing: EditorSizing = {
            fixed: p.height !== undefined,
            maxHeight: toCssLength(p.maxHeight),
          };
          const handle = mod.buildEditor({
            parent: container,
            doc: p.value,
            baseLanguage: p.baseLanguage ?? 'text',
            readOnly: p.readOnly ?? false,
            placeholder: p.placeholder,
            ariaLabel: p['aria-label'],
            palette: paletteRef.current,
            sizing,
            dataBox: dataBoxRef.current,
            persistenceKey: p.persistenceKey,
            onDocChanged: (doc) => callbacksRef.current.docChanged(doc),
            onFocusChanged: (hasFocus) => callbacksRef.current.focusChanged(hasFocus),
          });
          coreRef.current = handle;
          // A persisted (cached) state may carry a stale doc — the prop wins.
          lastEmittedRef.current = p.value;
          if (handle.getValue() !== p.value) handle.setValue(p.value);
          pendingRef.current = null;
          if (p.diagnostics && p.diagnostics.length > 0) {
            handle.setTemplateDiagnostics(p.diagnostics);
          }
          setReady(true);
        })
        .catch((error: unknown) => {
          // eslint-disable-next-line no-console
          console.error('TemplateCodeEditor: failed to load editor core', error);
          if (!cancelled) setFailed(true);
        });

      return () => {
        cancelled = true;
        callbacks.flush(); // don't lose in-flight edits
        coreRef.current?.destroy(); // persists state when persistenceKey is set
        coreRef.current = null;
      };
      // Intentionally create-once; live props flow via refs/handle effects.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---- prop → editor reconciliation
    useEffect(() => {
      coreRef.current?.setReadOnly(readOnly);
    }, [readOnly, ready]);

    useEffect(() => {
      coreRef.current?.setTemplateDiagnostics(diagnostics ?? null);
    }, [diagnostics, ready]);

    useEffect(() => {
      const core = coreRef.current;
      if (!core) return;
      if (value === lastEmittedRef.current) return; // our own (already emitted) edit
      if (value === core.getValue()) {
        lastEmittedRef.current = value;
        return;
      }
      // External change wins: drop pending local edits, replace the doc once.
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      pendingRef.current = null;
      lastEmittedRef.current = value;
      core.setValue(value);
    }, [value, ready]);

    useImperativeHandle(
      ref,
      () => ({
        insertAtCursor(text: string) {
          coreRef.current?.insertAtCursor(text);
        },
        getValue() {
          const core = coreRef.current;
          if (core) return core.getValue();
          return pendingRef.current ?? propsRef.current.value;
        },
        focus() {
          coreRef.current?.focus();
        },
        revealLine(line: number) {
          coreRef.current?.revealLine(line);
        },
        isReady() {
          return coreRef.current !== null;
        },
      }),
      [],
    );

    const fixedHeight = toCssLength(height);
    const skeletonHeight = typeof height === 'number' ? height : 120;

    return (
      <Box
        sx={{
          position: 'relative',
          border: 1,
          borderColor: focused ? 'primary.main' : 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          // `revealLine` scrolls this element to the top of the viewport; the
          // AppBar is fixed, so leave room for it.
          scrollMarginTop: 80,
          height: fixedHeight,
          maxHeight: fixedHeight === undefined ? toCssLength(maxHeight) : undefined,
        }}
      >
        {!ready && !failed && (
          <Skeleton
            variant="rounded"
            sx={{ position: 'absolute', inset: 0, zIndex: 1, height: '100%' }}
            height={skeletonHeight}
          />
        )}
        {failed && (
          <Box sx={{ p: 1.5, color: 'error.main', typography: 'body2' }}>
            Failed to load the template editor.
          </Box>
        )}
        <div
          ref={containerRef}
          style={{ height: fixedHeight !== undefined ? '100%' : undefined, minHeight: ready ? undefined : skeletonHeight }}
          data-testid="template-code-editor"
        />
      </Box>
    );
  },
);
